import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Gauge } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const CH = [["email", "Email"], ["sms", "SMS"], ["whatsapp", "WhatsApp"]];
const BAR = { ok: "bg-emerald-500", warn: "bg-amber-500", over: "bg-rose-600" };
const TXT = { ok: "text-slate-600", warn: "text-amber-700", over: "text-rose-700" };

export const useMsgUsage = () => {
  const { user } = useAuth();
  const [u, setU] = useState(null);
  const load = useCallback(() => { if (user && user.role !== "superadmin") api.get("/me/msg-usage").then((r) => setU(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { load(); window.addEventListener("crm-msg-sent", load); return () => window.removeEventListener("crm-msg-sent", load); }, [load]);
  return [u, load];
};

export const notifyMsgSent = () => window.dispatchEvent(new Event("crm-msg-sent"));

export const quotaRemaining = (u, channel) => {
  if (!u) return null;
  const c = u.profile?.channels?.[channel];
  const vals = [];
  if (c?.plafond) vals.push(c.remaining);
  if (u.cap) vals.push(Math.max(u.remaining, 0));
  return vals.length ? Math.min(...vals) : null;
};

const Bar = ({ k, l, c, compact }) => (
  <div data-testid={`quota-${k}`} data-state={c.state}>
    <div className={`flex items-center justify-between text-[11px] ${TXT[c.state]}`}><span className="font-medium">{l}</span><span className="tabular-nums">{c.used}{c.plafond ? ` / ${c.plafond}` : " · illimitato"}</span></div>
    {c.plafond > 0 && <div className={`${compact ? "mt-0.5 h-1" : "mt-1 h-1.5"} overflow-hidden rounded-full bg-slate-100`}><div className={`h-full rounded-full transition-[width] ${BAR[c.state]}`} style={{ width: `${Math.min(c.pct, 100)}%` }} /></div>}
  </div>
);

export const QuotaBars = ({ usage, compact = false }) => {
  const p = usage?.profile;
  if (!usage || !usage.messaging_enabled) return usage ? <div className="text-[11px] text-slate-400" data-testid="quota-disabled">Comunicazioni non abilitate per il tuo profilo</div> : null;
  const chans = CH.filter(([k]) => usage.channels?.[k] !== false);
  if (!p && !usage.cap) return null;
  return (
    <div className={compact ? "space-y-1.5" : "space-y-3"} data-testid="quota-bars" data-alert={p?.alert || "ok"}>
      {p && chans.map(([k, l]) => <Bar key={k} k={k} l={l} c={p.channels[k]} compact={compact} />)}
      {usage.cap > 0 && <Bar k="personal" l="Tetto personale (tutti i canali)" c={{ used: usage.used, plafond: usage.cap, pct: Math.round(usage.used * 100 / usage.cap), state: usage.used >= usage.cap ? "over" : usage.used * 100 >= usage.cap * (p?.warn_pct || 80) ? "warn" : "ok" }} compact={compact} />}
      {p?.alert === "over" && <div className="flex items-center gap-1 text-[11px] font-medium text-rose-700" data-testid="quota-alert-over"><AlertTriangle className="h-3 w-3" />Plafond esaurito: invii bloccati</div>}
      {p?.alert === "warn" && <div className="flex items-center gap-1 text-[11px] font-medium text-amber-700" data-testid="quota-alert-warn"><AlertTriangle className="h-3 w-3" />Plafond quasi esaurito</div>}
    </div>
  );
};

export const SidebarQuota = () => {
  const [u] = useMsgUsage();
  if (!u || !u.profile || !Object.values(u.profile.channels).some((c) => c.plafond > 0)) return null;
  return (
    <Link to="/comunicazioni" className="mx-3 mb-2 block rounded-lg border border-slate-200 bg-slate-50 p-2.5 transition-colors hover:bg-white" data-testid="sidebar-quota">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><Gauge className="h-3.5 w-3.5" />Messaggi {u.profile.mode === "recharge" ? "pacchetto" : "del mese"}</div>
      <QuotaBars usage={u} compact />
    </Link>
  );
};
